import React, { useState } from 'react';
import * as Sentry from '@sentry/react';

interface SentryTestProps {
  onTestError?: () => void;
}

const SentryTest: React.FC<SentryTestProps> = ({ onTestError }) => {
  const [testResults, setTestResults] = useState<string[]>([]);

  const addResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testError = () => {
    try {
      // Test d'erreur Sentry
      Sentry.captureException(new Error('Test error from SentryTest component'));
      addResult('✅ Erreur envoyée à Sentry');
      onTestError?.();
    } catch (error) {
      addResult('❌ Erreur lors du test Sentry');
    }
  };

  const testMessage = () => {
    try {
      // Test de message Sentry
      Sentry.captureMessage('Test message from SentryTest component', 'info');
      addResult('✅ Message envoyé à Sentry');
    } catch (error) {
      addResult('❌ Erreur lors de l\'envoi du message');
    }
  };

  const testBreadcrumb = () => {
    try {
      // Test de breadcrumb Sentry
      Sentry.addBreadcrumb({
        message: 'Test breadcrumb added',
        level: 'info',
        category: 'test'
      });
      Sentry.captureMessage('Message with breadcrumb', 'info');
      addResult('✅ Breadcrumb et message envoyés');
    } catch (error) {
      addResult('❌ Erreur lors du test breadcrumb');
    }
  };

  const testEnvironment = () => {
    const mode = process.env.REACT_APP_MODE;
    const nodeEnv = process.env.NODE_ENV;
    
    // Mapping comme dans sentry.ts
    const getSentryEnvironment = () => {
      switch (mode) {
        case 'DEV':
          return 'development';
        case 'PROD':
          return 'production';
        case 'STAGING':
          return 'staging';
        default:
          return 'development';
      }
    };
    
    const environment = getSentryEnvironment();
    const isProduction = environment === 'production';
    
    addResult(`🌍 REACT_APP_MODE: ${mode || 'non défini'}`);
    addResult(`🔧 NODE_ENV: ${nodeEnv || 'non défini'}`);
    addResult(`📋 Sentry Environment: ${environment}`);
    addResult(`⚡ Sentry activé: OUI (toujours)`);
    addResult(`📊 Sample Rate: ${isProduction ? '10%' : '100%'}`);
    addResult(`🎬 Replay activé: OUI`);
    addResult(`📹 Replay sessions: ${isProduction ? '10%' : '100%'}`);
    addResult(`🚨 Replay erreurs: ${isProduction ? '10%' : '100%'}`);
  };

  const testUserContext = () => {
    try {
      Sentry.setUser({
        id: 'test-user-123',
        username: 'test-user',
        email: 'test@example.com'
      });
      Sentry.captureMessage('Test avec contexte utilisateur', 'info');
      addResult('✅ Contexte utilisateur défini et message envoyé');
    } catch (error) {
      addResult('❌ Erreur lors du test contexte utilisateur');
    }
  };

  const testTags = () => {
    try {
      Sentry.setTag('test-tag', 'test-value');
      Sentry.setTag('component', 'SentryTest');
      Sentry.captureMessage('Test avec tags personnalisés', 'info');
      addResult('✅ Tags définis et message envoyé');
    } catch (error) {
      addResult('❌ Erreur lors du test tags');
    }
  };

  const testReplay = () => {
    try {
      // Simuler des interactions pour le replay
      addResult('🎬 Démarrage du test replay...');
      
      // Ajouter des breadcrumbs pour simuler une session
      Sentry.addBreadcrumb({
        message: 'Utilisateur clique sur le bouton de test',
        level: 'info',
        category: 'user-interaction'
      });
      
      // Simuler une erreur pour déclencher le replay d'erreur
      setTimeout(() => {
        Sentry.captureException(new Error('Test replay - erreur simulée'));
        addResult('✅ Erreur simulée pour test replay');
      }, 1000);
      
      addResult('📹 Replay en cours d\'enregistrement...');
      addResult('⏱️ Attendez 1 seconde pour voir l\'erreur');
    } catch (error) {
      addResult('❌ Erreur lors du test replay');
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div style={{ 
      padding: '20px', 
      border: '1px solid #ccc', 
      borderRadius: '8px',
      margin: '20px',
      backgroundColor: '#f9f9f9',
      maxWidth: '800px'
    }}>
      <h3>🧪 Test Sentry</h3>
      <p>Ces boutons permettent de tester la configuration Sentry :</p>
      
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <button 
          onClick={testEnvironment}
          style={{
            backgroundColor: '#6f42c1',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🌍 Test Environnement
        </button>
        <button 
          onClick={testError}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ❌ Test Erreur
        </button>
        <button 
          onClick={testMessage}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          💬 Test Message
        </button>
        <button 
          onClick={testBreadcrumb}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🍞 Test Breadcrumb
        </button>
        <button 
          onClick={testUserContext}
          style={{
            backgroundColor: '#fd7e14',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          👤 Test Utilisateur
        </button>
        <button 
          onClick={testTags}
          style={{
            backgroundColor: '#20c997',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🏷️ Test Tags
        </button>
        <button 
          onClick={testReplay}
          style={{
            backgroundColor: '#e83e8c',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🎬 Test Replay
        </button>
        <button 
          onClick={clearResults}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🗑️ Effacer
        </button>
      </div>

      {testResults.length > 0 && (
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '10px',
          marginBottom: '10px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>📋 Résultats des tests :</h4>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {testResults.map((result, index) => (
              <div key={index} style={{ 
                fontSize: '12px', 
                marginBottom: '2px',
                fontFamily: 'monospace'
              }}>
                {result}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ 
        fontSize: '12px', 
        color: '#666', 
        backgroundColor: '#e9ecef',
        padding: '10px',
        borderRadius: '4px'
      }}>
        <strong>ℹ️ Informations :</strong><br/>
        • REACT_APP_MODE mapping : DEV→development, PROD→production, STAGING→staging<br/>
        • Les erreurs sont envoyées à Sentry ET loggées dans la console<br/>
        • Replay et traces : 100% en dev/staging, 10% en production<br/>
        • Sample rate adaptatif selon l'environnement mappé<br/>
        • DSN: https://606672e713bd266870e96301683c8986@sentry.mjb.ovh/27
      </div>
    </div>
  );
};

export default SentryTest;

